import React from 'react';
import type { ComponentProps } from 'react';
import styled from '@emotion/styled';
import type {
  AlignItemsProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  DisplayProps,
  JustifyContentProps,
  OpacityProps,
  ResponsiveValue,
  SpaceProps,
} from 'styled-system';
import {
  alignItems,
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  display,
  justifyContent,
  opacity,
  position,
  space,
  themeGet,
} from 'styled-system';

import type { BackgroundColorProps } from '../../../theme/system-types';
import type {
  CursorProps,
  ThemeHeightProps,
  ThemeWidthProps,
} from '../../../theme/system-utilities';
import { cursor, themeHeight, themeWidth } from '../../../theme/system-utilities';
import { Flex } from '../../Flex';

export interface ControlButtonProps
  extends
    SpaceProps,
    JustifyContentProps,
    AlignItemsProps,
    DisplayProps,
    BorderProps,
    BorderColorProps,
    BorderRadiusProps {
  withOpacity?: boolean;
  shiftY?: boolean;
  onClick?: (e: Event) => void;
  disabled?: boolean;
  isVisualNavigation?: boolean;
  controlShadow?: string;
  arrowSize?: string | number;
  themeWidth?: ResponsiveValue<string | number>;
  themeHeight?: ResponsiveValue<string | number>;
  positionTop?: ResponsiveValue<string>;
}

const UnstyledControlButton = styled(Flex)<
  Pick<ControlButtonProps, 'arrowSize' | 'controlShadow'> &
    DisplayProps &
    ThemeWidthProps &
    ThemeHeightProps &
    BorderProps &
    BorderStyleProps &
    BorderWidthProps &
    BorderColorProps &
    BorderRadiusProps &
    BackgroundColorProps &
    CursorProps &
    OpacityProps
>`
  -webkit-appearance: none;
  ${display};
  ${justifyContent};
  ${alignItems};
  ${themeWidth};
  ${themeHeight};
  ${space};
  ${borderStyle};
  ${borderWidth};
  ${borderColor};
  ${border};
  ${borderRadius};
  ${position};
  ${backgroundColor};
  ${cursor};
  ${opacity};

  box-shadow: ${({ controlShadow, ...props }) =>
    controlShadow || `0 0 0 1px ${themeGet('colors.sys.neutral.background.inverted')(props)}`};

  &:hover {
    opacity: 1;
  }

  &:active,
  &:focus {
    outline: none;
  }

  &:active {
    border: 1px solid ${themeGet('colors.sys.neutral.border.default')};
    box-shadow: none;
  }
  &:focus {
    border: 2px solid ${themeGet('colors.sys.secondary.border.focus')};
  }

  &[disabled] {
    opacity: 0.5;
  }

  &[disabled] {
    display: none;
  }
`;

const ControlButton = (props: ComponentProps<typeof UnstyledControlButton>) => (
  <UnstyledControlButton
    as="button"
    display={['none', 'flex']}
    justifyContent="center"
    alignItems="center"
    themeWidth={6}
    themeHeight={6}
    arrowSize="22px"
    p={0}
    borderStyle="none"
    borderRadius="circle"
    backgroundColor="sys.neutral.background.default"
    cursor="pointer"
    opacity={0.9}
    {...props}
  />
);

export default ControlButton;
